/**
 * Booking plans + per-shop entitlement helpers.
 *
 * Plans are data, not constants: the app owner edits limits in the admin
 * console and they are stored server-side (mock: localStorage).
 * `null` limit = unlimited.
 */

export const DEFAULT_PLANS = {
    free: {
        id: "free",
        label: "Free",
        resourceLimit: 1,
        typeLimit: 1,
        price: 0,
        currency: "USD",
        description: "1 resource type (1 calendar), 1 bookable resource.",
    },
    pro: {
        id: "pro",
        label: "Pro",
        resourceLimit: 10,
        typeLimit: null,
        price: 499,
        currency: "PHP",
        description: "Multiple resource types and bookable resources.",
    },
};

export function getPlan(plans, planId) {
    if (plans && plans[planId]) return plans[planId];
    return DEFAULT_PLANS[planId] || DEFAULT_PLANS.free;
}

export function fmtLimit(n) {
    return n === null || n === undefined || n === Infinity ? "∞" : String(n);
}

function numOrNull(v, fallback) {
    if (v === null || v === undefined || v === "") return fallback;
    const n = parseInt(v, 10);
    return Number.isFinite(n) ? Math.max(0, n) : fallback;
}

/** Effective resource cap for a shop (override wins, else plan). */
export function effectiveLimit(shop, plans) {
    if (!shop) return 1;
    if (shop.resourceLimitOverride !== null && shop.resourceLimitOverride !== undefined) {
        return numOrNull(shop.resourceLimitOverride, 1);
    }
    const p = getPlan(plans, shop.plan);
    return p.resourceLimit === null || p.resourceLimit === undefined ? Infinity : p.resourceLimit;
}

/** Effective resource-type cap for a shop (override wins, else plan). */
export function effectiveTypeLimit(shop, plans) {
    if (!shop) return 1;
    if (shop.typeLimitOverride !== null && shop.typeLimitOverride !== undefined) {
        const v = numOrNull(shop.typeLimitOverride, 1);
        return v;
    }
    const p = getPlan(plans, shop.plan);
    if (p.typeLimit === null || p.typeLimit === undefined) return Infinity;
    return p.typeLimit;
}

export function shopTypes(allTypes, shopId) {
    return (allTypes || []).filter((t) => t.ownerId === shopId);
}

export function shopResources(allResources, shopId) {
    return (allResources || []).filter((r) => r.ownerId === shopId);
}

/**
 * Oldest-N resources stay bookable. Extras are locked (read-only).
 * Downgrade strategy: freeze, don't delete.
 */
export function splitShopResources(shop, allResources, plans) {
    const mine = shopResources(allResources, shop.id);
    const limit = effectiveLimit(shop, plans);
    const sorted = [...mine].sort((a, b) =>
        String(a.createdAt || a.id || "").localeCompare(String(b.createdAt || b.id || ""))
    );
    const cap = limit === Infinity ? sorted.length : limit;
    return { mine, limit, allowed: sorted.slice(0, cap), locked: sorted.slice(cap) };
}

export function isResourceLocked(shop, resourceId, allResources, plans) {
    const { allowed } = splitShopResources(shop, allResources, plans);
    return !allowed.some((r) => r.id === resourceId);
}

/** Types beyond the allowance (newest first). */
export function excessShopTypes(shop, allTypes, plans) {
    const mine = shopTypes(allTypes, shop.id).sort((a, b) =>
        String(a.createdAt || a.id || "").localeCompare(String(b.createdAt || b.id || ""))
    );
    const limit = effectiveTypeLimit(shop, plans);
    if (limit === Infinity) return [];
    return mine.slice(limit);
}

export function genApiKey(prefix) {
    const chars = "abcdefghijklmnopqrstuvwxyz0123456789";
    let out = "";
    for (let i = 0; i < 12; i++) out += chars[Math.floor(Math.random() * chars.length)];
    return `hk_live_${(prefix || "shop").toLowerCase().replace(/[^a-z]+/g, "") || "shop"}_${out}`;
}
